import { useCallback, useState, useEffect, useRef } from 'react';
import { BLEService } from 'web/services/BLEService';
import { useBleDeviceStore } from 'web/stores/bleDeviceStore';
import { log } from 'web/utils/logger';
import { useBleDeviceSyncWithoutLabels } from './useBleDeviceSyncWithoutLabels';
import { sendGet } from 'web/utils/request';
import { Device } from 'web/model/Device';

type NotificationType = 'error' | 'warning' | 'success' | 'info';
type Notification = {
    message: string;
    type: NotificationType;
    timestamp: number;
    deviceId?: string;
    deviceName?: string;
};

// Keep track of connected devices and their GATT servers
interface ConnectedDevice {
    device: BluetoothDevice;
    server?: BluetoothRemoteGATTServer;
    organizationId: string;
    patientId?: string;
    lastConnected: number;
    reconnectionAttempts: number;
    isConnecting: boolean;
}

// Type declarations for the patched BLEService methods
declare module 'web/services/BLEService' {
    interface BLEServiceExtensions {
        addProgressListener?: (callback: (fileId: number, progress: number) => void) => 
            (fileId: number, progress: number) => void;
        onFileTransferred?: (callback: (file: File) => Promise<void>) => 
            (file: File) => Promise<void>;
    }
}

export const useBleDeviceMonitor = () => {
    const { 
        monitoredDevices, 
        isDeviceMonitored,
        getMonitoringInfo,
        startMonitoring: storeStartMonitoring,
        stopMonitoring: storeStopMonitoring
    } = useBleDeviceStore();
    
    const [notification, setNotification] = useState<Notification | null>(null);
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [monitoredDeviceStatuses, setMonitoredDeviceStatuses] = useState<Record<string, boolean>>({});
    const [isActiveRecording, setIsActiveRecording] = useState<Record<string, boolean>>({});
    const [syncStatus, setSyncStatus] = useState<Record<string, {
        isAutoSyncing: boolean;
        syncStage: string;
        lastSyncTime?: number;
        syncProgress?: number;
        filesTransferred?: number;
    }>>({});
    const { handleSyncDevice } = useBleDeviceSyncWithoutLabels();
    
    // Store stop functions for each monitored device
    const stopMonitoringFunctions = useRef<Record<string, (() => Promise<void>) | null>>({});
    
    // Store connected devices with their GATT servers
    const connectedDevices = useRef<Map<string, ConnectedDevice>>(new Map());

    const showNotification = useCallback(
        (message: string, type: NotificationType = 'error', autoClear: boolean = true, deviceId?: string, deviceName?: string) => {
            const newNotification: Notification = { 
                message, 
                type,
                timestamp: Date.now(),
                deviceId,
                deviceName 
            };

            // Update the current notification
            setNotification(newNotification);
            
            // Add to notifications history
            setNotifications(prev => {
                // Limit to 20 most recent notifications
                const updated = [newNotification, ...prev];
                if (updated.length > 20) {
                    return updated.slice(0, 20);
                }
                return updated;
            });
            
            // Play sound for important notifications
            if (type === 'error' || type === 'warning') {
                try {
                    // Try to play a notification sound
                    const audio = new Audio();
                    if (type === 'error') {
                        audio.src = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj2a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1N/Xt3FKKXHDx+bNmX5nW5a4zOP1494AAJKga3F7kKyuoIRwWWRwgJOapZ6NgWp6kKe1qZKDb4FwdHJ1eHp5eHGwycW2iVxPb6jh+dOHSzFVi8n2zbJbLiBboOz/2Z5SLTFxu/Hmr2g9KUMBAAAAAQAAAAAAAAAAAAAAAAAAAAH//wAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA//8AAP//AAAAAAAA/wD///7///8AAP//AAAAAP//';
                    } else {
                        audio.src = 'data:audio/wav;base64,UklGRl9WTkRSTVNHAAAAA2QBHwAATElTVCRJTkZPSVNGVENPTQAAAEl2YW4gRG9lAElTRlQpTEF2YyBMaWJyYXJ5OiBzb3guYXYgKENmZzogbXA0YS9lZ2FhYykAAAAAAAAADABEQVRBdqvStR1FSKQzM7vY7XY7u93uLKKS7u7uIZFh7u7uYWfv7u7u7iEd1N3d3d3DNTU1NTMzMzPF3d3d4TwigkREJCWhoaE=';
                    }
                    audio.volume = 0.5;
                    audio.play().catch(e => console.log("Couldn't play notification sound:", e));
                } catch (e) {
                    console.log("Error playing notification sound:", e);
                }
                
                // Try to use the web notifications API if permitted
                if (type === 'warning' && "Notification" in window && Notification.permission === "granted") {
                    try {
                        new Notification("NXVET Device Alert", {
                            body: message,
                            icon: "/favicon.ico"
                        });
                    } catch (e) {
                        console.log("Error showing browser notification:", e);
                    }
                }
            }
            
            if (autoClear) {
                setTimeout(() => {
                    setNotification(prev => 
                        // Only clear if it's the same notification
                        prev?.timestamp === newNotification.timestamp ? null : prev
                    );
                }, 5000);
            }
        },
        []
    );

    // Request notification permission on mount
    useEffect(() => {
        if ("Notification" in window && Notification.permission !== "granted" && Notification.permission !== "denied") {
            Notification.requestPermission();
        }
    }, []);

    // Function to update sync status for a specific device
    const updateSyncStatus = useCallback((deviceId: string, update: Partial<{
        isAutoSyncing: boolean;
        syncStage: string;
        lastSyncTime?: number;
        syncProgress?: number;
        filesTransferred?: number;
    }>) => {
        setSyncStatus(prev => ({
            ...prev,
            [deviceId]: {
                ...(prev[deviceId] || { 
                    isAutoSyncing: false, 
                    syncStage: 'idle',
                    filesTransferred: 0
                }),
                ...update
            }
        }));
    }, []);

    // Helper function to establish and maintain connection
    const establishConnection = useCallback(async (device: BluetoothDevice, organizationId: string, patientId?: string) => {
        const deviceId = device.id;
        const deviceInfo = connectedDevices.current.get(deviceId);
        
        // If we're already connecting, don't start another connection attempt
        if (deviceInfo?.isConnecting) {
            return;
        }
        
        try {
            // Update status to connecting
            connectedDevices.current.set(deviceId, {
                device,
                organizationId,
                patientId,
                lastConnected: Date.now(),
                reconnectionAttempts: deviceInfo?.reconnectionAttempts || 0,
                isConnecting: true
            });
            
            log(`Establishing persistent connection to device: ${deviceId}`);
            
            // Use the built-in gatt.connect() instead of our custom connect
            const server = await device.gatt?.connect();
            if (!server) throw new Error('Failed to connect to GATT server');
            
            // Successfully connected
            connectedDevices.current.set(deviceId, {
                device,
                server,
                organizationId,
                patientId,
                lastConnected: Date.now(),
                reconnectionAttempts: 0,  // Reset attempts on successful connection
                isConnecting: false
            });
            
            // Setup disconnection listener
            device.addEventListener('gattserverdisconnected', async () => {
                log(`Device ${deviceId} disconnected - attempting to reconnect`);
                
                const currentDevice = connectedDevices.current.get(deviceId);
                if (currentDevice && isDeviceMonitored(deviceId)) {
                    // Don't try to reconnect if we're already connecting
                    if (currentDevice.isConnecting) return;
                    
                    const reconnectDelay = Math.min(currentDevice.reconnectionAttempts * 1000, 10000);
                    
                    // Update reconnection attempts
                    connectedDevices.current.set(deviceId, {
                        ...currentDevice,
                        reconnectionAttempts: currentDevice.reconnectionAttempts + 1,
                        server: undefined
                    });
                    
                    showNotification(
                        `🔌 Connection lost to ${device.name || 'Unknown Device'}`, 
                        'warning',
                        true,
                        deviceId,
                        device.name || 'Unknown Device'
                    );
                    
                    // Wait before reconnecting to avoid rapid reconnection attempts
                    setTimeout(() => {
                        if (isDeviceMonitored(deviceId)) {
                            establishConnection(device, currentDevice.organizationId, currentDevice.patientId)
                                .then(async (server) => {
                                    if (server) {
                                        // Re-register for notifications after successful reconnection
                                        try {
                                            // If there was a previous stop function, call it to clean up
                                            if (stopMonitoringFunctions.current[deviceId]) {
                                                await stopMonitoringFunctions.current[deviceId]?.();
                                            }
                                            
                                            // Re-register for active recording notifications
                                            const stopMonitoringFunc = await BLEService.startMonitoringActiveRecording(
                                                device,
                                                (isRecording) => {
                                                    log(`Received active recording notification: ${isRecording}`);
                                                    
                                                    // Update recording status state
                                                    setIsActiveRecording(prev => ({
                                                        ...prev,
                                                        [device.id]: isRecording
                                                    }));
                                                    
                                                    // Always show a notification when the recording status changes
                                                    if (isRecording) {
                                                        showNotification(
                                                            `📊 Recording on ${device.name || 'Unknown Device'}`, 
                                                            'warning',
                                                            true,
                                                            device.id,
                                                            device.name || 'Unknown Device'
                                                        );
                                                        
                                                        // If recording is active, trigger a sync
                                                        const deviceInfo = getMonitoringInfo(device.id);
                                                        if (deviceInfo?.organizationId) {
                                                            // Show notification
                                                            showNotification(
                                                                `🔄 Auto-syncing device`, 
                                                                'info',
                                                                true,
                                                                device.id,
                                                                device.name || 'Unknown Device'
                                                            );
                                                            
                                                            // Auto-sync the device
                                                            const deviceSerial = device.name;
                                                            let isOnboarded = false;
                                                            if (deviceSerial) {
                                                                sendGet(`/api/devices?limit=1&deviceSerial=${deviceSerial}`).then((devices: Device[]) => {
                                                                    if (devices && devices.length > 0) {
                                                                        const serverDevice = new Device(devices[0]);
                                                                        isOnboarded = serverDevice.isOnboarded ?? false;
                                                                    }
                                                                    handleSyncDevice(device, !isOnboarded)
                                                                        .then(() => {
                                                                            updateSyncStatus(device.id, {
                                                                                isAutoSyncing: false,
                                                                                syncStage: 'Auto-sync complete',
                                                                                lastSyncTime: Date.now()
                                                                            });
                                                                        })
                                                                        .catch(error => {
                                                                            log('Error auto-syncing device:', error);
                                                                            updateSyncStatus(device.id, {
                                                                                isAutoSyncing: false,
                                                                                syncStage: 'Auto-sync failed'
                                                                            });
                                                                            showNotification(
                                                                                `❌ Auto-sync failed: ${(error as Error).message}`,
                                                                                'error',
                                                                                true,
                                                                                device.id,
                                                                                device.name || 'Unknown Device'
                                                                            );
                                                                        });
                                                                });
                                                            }
                                                        }
                                                    } else {
                                                        showNotification(
                                                            `✅ Recording ended`, 
                                                            'success',
                                                            true,
                                                            device.id,
                                                            device.name || 'Unknown Device'
                                                        );
                                                        
                                                        // If the recording just stopped, we trigger a sync
                                                        if (deviceInfo?.organizationId) {
                                                            // Show notification
                                                            showNotification(
                                                                `🔄 Auto-syncing device`, 
                                                                'info',
                                                                true,
                                                                device.id,
                                                                device.name || 'Unknown Device'
                                                            );
                                                            
                                                            // Auto-sync the device
                                                            const deviceSerial = device.name;
                                                            let isOnboarded = false;
                                                            if (deviceSerial) {
                                                                sendGet(`/api/devices?limit=1&deviceSerial=${deviceSerial}`).then((devices: Device[]) => {
                                                                    if (devices && devices.length > 0) {
                                                                        const serverDevice = new Device(devices[0]);
                                                                        isOnboarded = serverDevice.isOnboarded ?? false;
                                                                    }
                                                                    handleSyncDevice(device, !isOnboarded)
                                                                        .then(() => {
                                                                            updateSyncStatus(device.id, {
                                                                                isAutoSyncing: false,
                                                                                syncStage: 'Auto-sync complete',
                                                                                lastSyncTime: Date.now()
                                                                            });
                                                                        })
                                                                        .catch(error => {
                                                                            log('Error auto-syncing device:', error);
                                                                            updateSyncStatus(device.id, {
                                                                                isAutoSyncing: false,
                                                                                syncStage: 'Auto-sync failed'
                                                                            });
                                                                            showNotification(
                                                                                `❌ Auto-sync failed: ${(error as Error).message}`,
                                                                                'error',
                                                                                true,
                                                                                device.id,
                                                                                device.name || 'Unknown Device'
                                                                            );
                                                                        });
                                                                });
                                                            }
                                                        }
                                                    }
                                                }
                                            );
                                            
                                            // Store the new stop function
                                            stopMonitoringFunctions.current[deviceId] = stopMonitoringFunc;
                                            
                                            // Update monitoring status
                                            setMonitoredDeviceStatuses(prev => ({
                                                ...prev,
                                                [device.id]: true
                                            }));
                                            
                                            showNotification(
                                                `✅ Reconnected to ${device.name || 'Unknown Device'}`, 
                                                'success',
                                                true,
                                                device.id,
                                                device.name || 'Unknown Device'
                                            );
                                            
                                            // Sync time with the device (skip for NXHUB devices)
                                            if (!device.name?.toUpperCase().startsWith('NXHUB')) {
                                                await BLEService.syncEpochTime(device).catch(error => {
                                                    log('Error syncing time after reconnection:', error);
                                                });
                                            }
                                            
                                            // Trigger auto-sync when connection resumes
                                            const reconnectedDeviceInfo = getMonitoringInfo(device.id);
                                            if (reconnectedDeviceInfo?.organizationId) {
                                                // Update sync status to indicate auto-sync is starting
                                                updateSyncStatus(device.id, {
                                                    isAutoSyncing: true,
                                                    syncStage: 'Starting auto-sync after reconnection...',
                                                    lastSyncTime: Date.now()
                                                });
                                                
                                                // Show notification
                                                showNotification(
                                                    `🔄 Auto-syncing after reconnection`, 
                                                    'info',
                                                    true,
                                                    device.id,
                                                    device.name || 'Unknown Device'
                                                );
                                                
                                                // Auto-sync the device
                                                const reconnectedDeviceSerial = device.name;
                                                let reconnectedIsOnboarded = false;
                                                if (reconnectedDeviceSerial) {
                                                    sendGet(`/api/devices?limit=1&deviceSerial=${reconnectedDeviceSerial}`).then((devices: Device[]) => {
                                                        if (devices && devices.length > 0) {
                                                            const serverDevice = new Device(devices[0]);
                                                            reconnectedIsOnboarded = serverDevice.isOnboarded ?? false;
                                                        }
                                                        handleSyncDevice(device, !reconnectedIsOnboarded)
                                                            .then(() => {
                                                                updateSyncStatus(device.id, {
                                                                    isAutoSyncing: false,
                                                                    syncStage: 'Auto-sync complete',
                                                                    lastSyncTime: Date.now()
                                                                });
                                                            })
                                                            .catch(error => {
                                                                log('Error auto-syncing device after reconnection:', error);
                                                                updateSyncStatus(device.id, {
                                                                    isAutoSyncing: false,
                                                                    syncStage: 'Auto-sync failed'
                                                                });
                                                                showNotification(
                                                                    `❌ Auto-sync failed: ${(error as Error).message}`,
                                                                    'error',
                                                                    true,
                                                                    device.id,
                                                                    device.name || 'Unknown Device'
                                                                );
                                                            });
                                                    }).catch(error => {
                                                        log('Error checking device onboarding status after reconnection:', error);
                                        // Still try to sync even if we couldn't check onboarding status
                                        handleSyncDevice(device, false)
                                            .then(() => {
                                                updateSyncStatus(device.id, {
                                                    isAutoSyncing: false,
                                                    syncStage: 'Auto-sync complete',
                                                    lastSyncTime: Date.now()
                                                });
                                            })
                                            .catch(syncError => {
                                                log('Error auto-syncing device after reconnection:', syncError);
                                                updateSyncStatus(device.id, {
                                                    isAutoSyncing: false,
                                                    syncStage: 'Auto-sync failed'
                                                });
                                                showNotification(
                                                    `❌ Auto-sync failed: ${(syncError as Error).message}`,
                                                    'error',
                                                    true,
                                                    device.id,
                                                    device.name || 'Unknown Device'
                                                );
                                            });
                                    });
                                                }
                                            }
                                        } catch (error) {
                                            log('Error re-registering for notifications after reconnection:', error);
                                            showNotification(
                                                `⚠️ Connected but failed to subscribe to notifications`, 
                                                'warning',
                                                true,
                                                device.id,
                                                device.name || 'Unknown Device'
                                            );
                                        }
                                    }
                                })
                                .catch(error => {
                                    log(`Failed to reconnect to device ${deviceId}:`, error);
                                });
                        }
                    }, reconnectDelay);
                }
            });
            
            log(`Persistent connection established to device: ${deviceId}`);
            return server;
            
        } catch (error) {
            // Failed to connect
            const currentDevice = connectedDevices.current.get(deviceId);
            if (currentDevice) {
                connectedDevices.current.set(deviceId, {
                    ...currentDevice,
                    isConnecting: false,
                    reconnectionAttempts: currentDevice.reconnectionAttempts + 1,
                    server: undefined
                });
            }
            
            log(`Failed to establish persistent connection to device: ${deviceId}`, error);
            throw error;
        }
    }, [isDeviceMonitored, showNotification, handleSyncDevice, getMonitoringInfo, updateSyncStatus]);

    // Function to start monitoring a device
    const startMonitoring = useCallback(
        async (device: BluetoothDevice, organizationId: string, patientId?: string) => {
            try {
                log(`Starting monitoring for device: ${device.id}`);
                
                // Store monitoring info in the store
                storeStartMonitoring(device, organizationId, patientId);
                
                // Establish persistent connection
                const server = await establishConnection(device, organizationId, patientId);
                
                if (!server) {
                    throw new Error('Failed to establish persistent connection');
                }
                
                // Start monitoring for active recording notifications
                const stopMonitoringFunc = await BLEService.startMonitoringActiveRecording(
                    device,
                    (isRecording) => {
                        log(`Received active recording notification: ${isRecording}`);
                        
                        // Update recording status state
                        setIsActiveRecording(prev => ({
                            ...prev,
                            [device.id]: isRecording
                        }));
                        
                        // Always show a notification when the recording status changes
                        if (isRecording) {
                            showNotification(
                                `📊 Recording on ${device.name || 'Unknown Device'}`, 
                                'warning',
                                true,
                                device.id,
                                device.name || 'Unknown Device'
                            );
                            
                            // If recording is active, trigger a sync
                            const deviceInfo = getMonitoringInfo(device.id);
                            if (deviceInfo?.organizationId) {
                                // Update sync status to indicate auto-sync is starting
                                updateSyncStatus(device.id, {
                                    isAutoSyncing: true,
                                    syncStage: 'Starting auto-sync...',
                                    lastSyncTime: Date.now()
                                });
                                
                                // Show notification
                                showNotification(
                                    `🔄 Auto-syncing device`, 
                                    'info',
                                    true,
                                    device.id,
                                    device.name || 'Unknown Device'
                                );
                                
                                // Auto-sync the device
                                const deviceSerial = device.name;
                                let isOnboarded = false;
                                if (deviceSerial) {
                                    sendGet(`/api/devices?limit=1&deviceSerial=${deviceSerial}`).then((devices: Device[]) => {
                                        if (devices && devices.length > 0) {
                                            const serverDevice = new Device(devices[0]);
                                            isOnboarded = serverDevice.isOnboarded ?? false;
                                        }
                                        handleSyncDevice(device, !isOnboarded)
                                            .then(() => {
                                                updateSyncStatus(device.id, {
                                                    isAutoSyncing: false,
                                                    syncStage: 'Auto-sync complete',
                                                    lastSyncTime: Date.now()
                                                });
                                            })
                                            .catch(error => {
                                                log('Error auto-syncing device:', error);
                                                updateSyncStatus(device.id, {
                                                    isAutoSyncing: false,
                                                    syncStage: 'Auto-sync failed'
                                                });
                                                showNotification(
                                                    `❌ Auto-sync failed: ${(error as Error).message}`,
                                                    'error',
                                                    true,
                                                    device.id,
                                                    device.name || 'Unknown Device'
                                                );
                                            });
                                    });
                                }
                            }
                        } else {
                            showNotification(
                                `✅ Recording ended`, 
                                'success',
                                true,
                                device.id,
                                device.name || 'Unknown Device'
                            );
                            
                            // Only clear auto-sync flag if not currently syncing
                            if (!syncStatus[device.id]?.isAutoSyncing) {
                                updateSyncStatus(device.id, {
                                    isAutoSyncing: false,
                                    syncStage: 'idle'
                                });
                            }
                        }
                    }
                );
                
                // Store the stop function
                stopMonitoringFunctions.current[device.id] = stopMonitoringFunc;
                
                // Update monitoring status
                setMonitoredDeviceStatuses(prev => ({
                    ...prev,
                    [device.id]: true
                }));
                
                showNotification(
                    `👂 Monitoring ${device.name || 'Unknown Device'}`, 
                    'success',
                    true,
                    device.id,
                    device.name || 'Unknown Device'
                );
                
                // Sync time with the device (skip for NXHUB devices)
                if (!device.name?.toUpperCase().startsWith('NXHUB')) {
                    await BLEService.syncEpochTime(device);
                }
                
                // Trigger auto-sync when monitoring starts
                const deviceInfo = getMonitoringInfo(device.id);
                if (deviceInfo?.organizationId) {
                    // Update sync status to indicate auto-sync is starting
                    updateSyncStatus(device.id, {
                        isAutoSyncing: true,
                        syncStage: 'Starting auto-sync...',
                        lastSyncTime: Date.now()
                    });
                    
                    // Show notification
                    showNotification(
                        `🔄 Auto-syncing device`, 
                        'info',
                        true,
                        device.id,
                        device.name || 'Unknown Device'
                    );
                    
                    // Auto-sync the device
                    const deviceSerial = device.name;
                    let isOnboarded = false;
                    if (deviceSerial) {
                        sendGet(`/api/devices?limit=1&deviceSerial=${deviceSerial}`).then((devices: Device[]) => {
                            if (devices && devices.length > 0) {
                                const serverDevice = new Device(devices[0]);
                                isOnboarded = serverDevice.isOnboarded ?? false;
                            }
                            handleSyncDevice(device, !isOnboarded)
                                .then(() => {
                                    updateSyncStatus(device.id, {
                                        isAutoSyncing: false,
                                        syncStage: 'Auto-sync complete',
                                        lastSyncTime: Date.now()
                                    });
                                })
                                .catch(error => {
                                    log('Error auto-syncing device:', error);
                                    updateSyncStatus(device.id, {
                                        isAutoSyncing: false,
                                        syncStage: 'Auto-sync failed'
                                    });
                                    showNotification(
                                        `❌ Auto-sync failed: ${(error as Error).message}`,
                                        'error',
                                        true,
                                        device.id,
                                        device.name || 'Unknown Device'
                                    );
                                });
                        }).catch(error => {
                            log('Error checking device onboarding status:', error);
                            // Still try to sync even if we couldn't check onboarding status
                            handleSyncDevice(device, false)
                                .then(() => {
                                    updateSyncStatus(device.id, {
                                        isAutoSyncing: false,
                                        syncStage: 'Auto-sync complete',
                                        lastSyncTime: Date.now()
                                    });
                                })
                                .catch(syncError => {
                                    log('Error auto-syncing device:', syncError);
                                    updateSyncStatus(device.id, {
                                        isAutoSyncing: false,
                                        syncStage: 'Auto-sync failed'
                                    });
                                    showNotification(
                                        `❌ Auto-sync failed: ${(syncError as Error).message}`,
                                        'error',
                                        true,
                                        device.id,
                                        device.name || 'Unknown Device'
                                    );
                                });
                        });
                    }
                }
            } catch (error) {
                log('Error starting monitoring:', error);
                showNotification(
                    `❌ Failed to start monitoring ${device.name || 'Unknown Device'}: ${(error as Error).message}`, 
                    'error',
                    true,
                    device.id,
                    device.name || 'Unknown Device'
                );
                storeStopMonitoring(device.id);
            }
        },
        [establishConnection, showNotification, handleSyncDevice, getMonitoringInfo, storeStartMonitoring, storeStopMonitoring, updateSyncStatus]
    );

    // Function to stop monitoring a device
    const stopMonitoring = useCallback(
        async (device: BluetoothDevice) => {
            try {
                const deviceId = device.id;
                log(`Stopping monitoring for device: ${deviceId}`);
                
                // Call the stored stop function
                if (stopMonitoringFunctions.current[deviceId]) {
                    await stopMonitoringFunctions.current[deviceId]?.();
                    stopMonitoringFunctions.current[deviceId] = null;
                }
                
                // Disconnect the persistent connection
                const deviceInfo = connectedDevices.current.get(deviceId);
                if (deviceInfo?.server?.connected) {
                    deviceInfo.server.disconnect();
                }
                
                // Remove from connected devices
                connectedDevices.current.delete(deviceId);
                
                // Remove from store
                storeStopMonitoring(deviceId);
                
                // Update monitoring status
                setMonitoredDeviceStatuses(prev => ({
                    ...prev,
                    [deviceId]: false
                }));
                
                // Also clear the active recording status
                setIsActiveRecording(prev => {
                    const newState = { ...prev };
                    delete newState[deviceId];
                    return newState;
                });
                
                showNotification(
                    `🛑 Stopped monitoring ${device.name || 'Unknown Device'}`, 
                    'info',
                    true,
                    deviceId,
                    device.name || 'Unknown Device'
                );
            } catch (error) {
                log('Error stopping monitoring:', error);
                showNotification(
                    `❌ Error stopping monitoring for ${device.name || 'Unknown Device'}: ${(error as Error).message}`, 
                    'error',
                    true,
                    device.id,
                    device.name || 'Unknown Device'
                );
            }
        },
        [showNotification, storeStopMonitoring]
    );

    // Set up connection health check interval
    useEffect(() => {
        const checkConnectionHealth = async () => {
            for (const [deviceId, deviceInfo] of connectedDevices.current.entries()) {
                // Skip if not being monitored or already reconnecting
                if (!isDeviceMonitored(deviceId) || deviceInfo.isConnecting) {
                    continue;
                }
                
                // Check if device is still connected
                const isConnected = deviceInfo.server?.connected;
                
                // Update UI immediately when connection status changes
                setMonitoredDeviceStatuses(prev => ({
                    ...prev,
                    [deviceId]: isConnected || false
                }));
                
                if (!isConnected) {
                    log(`Device ${deviceId} connection lost - attempting to reconnect`);
                    
                    try {
                        // Attempt to reestablish connection
                        await establishConnection(
                            deviceInfo.device, 
                            deviceInfo.organizationId, 
                            deviceInfo.patientId
                        );
                    } catch (error) {
                        log(`Failed to reconnect to device ${deviceId}:`, error);
                    }
                }
            }
        };
        
        // Check connection health more frequently for responsive UI (every 10 seconds)
        const intervalId = setInterval(checkConnectionHealth, 10000);
        
        return () => {
            clearInterval(intervalId);
        };
    }, [establishConnection, isDeviceMonitored]);

    // Auto-connect to all monitored devices on component mount
    useEffect(() => {
        const connectToMonitoredDevices = async () => {
            log(`${monitoredDevices.size} devices should be monitored.`);
            
            // We can't auto-connect to monitored devices without user interaction
            // This information is just for reference, user will need to manually 
            // click to reconnect monitored devices
        };
        
        connectToMonitoredDevices();
        
        // Clean up all monitoring on unmount
        return () => {
            // Disconnect all connected devices and clear monitoring
            for (const [deviceId, deviceInfo] of connectedDevices.current.entries()) {
                try {
                    if (deviceInfo.server?.connected) {
                        deviceInfo.server.disconnect();
                    }
                    
                    const stopFunc = stopMonitoringFunctions.current[deviceId];
                    if (stopFunc) {
                        stopFunc().catch(error => {
                            console.error(`Error cleaning up monitoring for device ${deviceId}:`, error);
                        });
                    }
                } catch (error) {
                    console.error(`Error disconnecting device ${deviceId}:`, error);
                }
            }
            
            // Clear maps
            connectedDevices.current.clear();
            stopMonitoringFunctions.current = {};
        };
    }, [monitoredDevices]);

    // Function to check if device is currently connected
    const isDeviceConnected = useCallback((deviceId: string) => {
        return connectedDevices.current.get(deviceId)?.server?.connected || false;
    }, []);

    // Add additional status detail function
    const getConnectionStatusDetails = useCallback((deviceId: string) => {
        const deviceInfo = connectedDevices.current.get(deviceId);
        
        if (!deviceInfo) {
            return {
                status: 'unknown',
                statusText: 'Unknown',
                lastConnectionAttempt: undefined,
                reconnectionAttempts: 0
            };
        }
        
        if (deviceInfo.isConnecting) {
            return {
                status: 'connecting',
                statusText: 'Connecting...',
                lastConnectionAttempt: deviceInfo.lastConnected,
                reconnectionAttempts: deviceInfo.reconnectionAttempts
            };
        }
        
        if (deviceInfo.server?.connected) {
            return {
                status: 'connected',
                statusText: 'Connected',
                lastConnectionAttempt: deviceInfo.lastConnected,
                reconnectionAttempts: deviceInfo.reconnectionAttempts
            };
        }
        
        return {
            status: 'disconnected',
            statusText: 'Disconnected',
            lastConnectionAttempt: deviceInfo.lastConnected,
            reconnectionAttempts: deviceInfo.reconnectionAttempts
        };
    }, []);

    // Function to restore monitored devices from localStorage
    const restoreMonitoredDevices = useCallback(async (
        monitoredDeviceInfo: Record<string, { deviceName: string, organizationId: string, patientId?: string }>
    ) => {
        log(`Restoring monitoring state for ${Object.keys(monitoredDeviceInfo).length} devices`);
        
        // Set up state for these devices without actually connecting yet
        // This will show them in the UI as monitored but disconnected
        for (const [deviceId, info] of Object.entries(monitoredDeviceInfo)) {
            // Add to our store first
            storeStartMonitoring({
                id: deviceId,
                name: info.deviceName
            } as BluetoothDevice, info.organizationId, info.patientId);
            
            // Set up the connection tracking state
            connectedDevices.current.set(deviceId, {
                device: {
                    id: deviceId,
                    name: info.deviceName
                } as BluetoothDevice,
                organizationId: info.organizationId,
                patientId: info.patientId,
                lastConnected: Date.now(),
                reconnectionAttempts: 0,
                isConnecting: false
            });
            
            // Update monitoring status in UI
            setMonitoredDeviceStatuses(prev => ({
                ...prev,
                [deviceId]: false // Start as disconnected
            }));
            
            // Show restored notification
            showNotification(
                `${info.deviceName} monitoring ready`,
                'info',
                true,
                deviceId,
                info.deviceName
            );
        }
    }, [storeStartMonitoring, showNotification]);

    return {
        startMonitoring,
        stopMonitoring,
        notification,
        notifications,
        isDeviceMonitored,
        isDeviceConnected,
        getConnectionStatusDetails,
        isActiveRecording,
        monitoredDeviceStatuses,
        syncStatus,
        clearNotifications: () => setNotifications([]),
        restoreMonitoredDevices
    };
}; 